import React, {useState, useCallback, useEffect} from 'react'
import flair from 'assets/svgs/flair-bundles/sq-bundle_01-03.svg'

import {Flex, Box, Grid} from 'src/components/Box'
import {Action} from 'src/components/Action'
import Article from 'src/components/Journey/Article'
import Loading from 'src/components/Loading'
import {CloseIcon} from 'src/components/icons'
import Button from 'src/components/Button'
import {Body, Lede} from 'src/components/text'
import BackgroundImage from 'src/components/BackgroundImage'
import SearchForm from 'src/components/SearchForm'
import useSearch, {SearchTarget, SearchSort} from 'src/hooks/useSearch'
import useSearchOnTargetChange from 'src/hooks/useSearchOnTargetChange'
import useSearchOnSortChange from 'src/hooks/useSearchOnSortChange'

import SearchContentOptions from './SearchContentOptions'
import VideoCard from './Journey/VideoCard'

const resultsPerPage = 5

const SearchContent: React.FunctionComponent<{onRequestClose: () => void}> = ({
  onRequestClose,
}) => {
  const [query, setQuery] = useState('')
  const [target, setTarget] = useState(SearchTarget.Algolia)
  const [, setPlaceholder] = useState('Search Articles')
  const [sort, setSort] = useState(SearchSort.Relevance)

  const {status, existingSearch, searchResults, hasMore, loadMore, search} =
    useSearch({
      resultsPerPage,
    })

  useEffect(() => {
    if (target === SearchTarget.Algolia) {
      setPlaceholder('Search Articles')
      setSort(SearchSort.Relevance)
    } else if (target === SearchTarget.YouTube) {
      setPlaceholder('Search Videos')
      setSort(SearchSort.Date)
    }
  }, [target])

  const performSearch = useCallback(() => {
    search({query, target, sort: SearchSort.Relevance})
  }, [search, query])

  useSearchOnTargetChange(target, status, existingSearch, search)
  useSearchOnSortChange(sort, status, existingSearch, search)

  const formDisabled =
    status === 'pending' ||
    !query ||
    (status === 'resolved' && query === existingSearch?.query)

  return (
    <Flex height="100%" flexDirection="column" position="relative">
      <BackgroundImage
        url={flair}
        height="100px"
        width="100px"
        position="absolute"
        center
      />

      <Flex p="3">
        <SearchForm
          query={query}
          setQuery={setQuery}
          performSearch={performSearch}
          disabled={formDisabled}
          autofocus
        />
        <Action onClick={onRequestClose} type="button">
          <CloseIcon ml="2" width="1.25rem" />
        </Action>
      </Flex>

      <Flex p="3" flexDirection="column" alignItems="flex-start">
        <SearchContentOptions target={target} onTargetChange={setTarget} />
      </Flex>

      <Grid p="3" gridGap="5" flex="1" overflowY="auto">
        {searchResults?.map(({title, summary, link, id, heroUrl}) => {
          if (target === SearchTarget.Algolia) {
            return (
              <Box key={id}>
                <Article
                  title={title}
                  description={summary}
                  link={link}
                ></Article>
              </Box>
            )
          } else {
            return (
              <Box key={id}>
                <VideoCard
                  title={title}
                  heroUrl={heroUrl}
                  time={''}
                  link={link}
                ></VideoCard>
              </Box>
            )
          }
        })}

        {status === 'pending' ? <Loading size="medium" /> : null}

        {status === 'resolved' && searchResults != null ? (
          <Flex pb="3" flexDirection="column">
            {searchResults.length === 0 ? (
              <Grid gridGap="3">
                <Lede css={{textAlign: 'center'}}>Oh no!</Lede>
                <Body>
                  We couldn't find content matching "{existingSearch?.query}".
                  Try searching again.
                </Body>
              </Grid>
            ) : null}

            {searchResults.length > 0 && !hasMore ? (
              <Body>Showing all results</Body>
            ) : null}

            {hasMore ? <Button onClick={loadMore}>Load more</Button> : null}
          </Flex>
        ) : null}
      </Grid>
    </Flex>
  )
}

export default SearchContent
